import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Minus, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Container, SectionHeading } from "./shared";

type QuestionProps = {
  question: string;
  answer: string;
};

function Question({ question, answer }: QuestionProps) {
  const [open, setOpen] = useState(false);

  return (
    <button
      onClick={() => setOpen(!open)}
      className="w-full rounded-2xl overflow-hidden bg-neutral-100 dark:bg-neutral-800 p-6 md:p-8 text-left"
    >
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-lg md:text-xl font-semibold text-foreground">
          {question}
        </h3>
        <div className="w-8 h-8 rounded-full relative bg-neutral-900 dark:bg-white flex items-center justify-center flex-shrink-0">
          <Minus
            className={cn(
              "w-4 h-4 text-white dark:text-neutral-900 absolute inset-0 m-auto transition-all duration-200",
              open ? "opacity-100 rotate-0" : "opacity-0 rotate-90"
            )}
          />
          <Plus
            className={cn(
              "w-4 h-4 text-white dark:text-neutral-900 absolute inset-0 m-auto transition-all duration-200",
              open ? "opacity-0 -rotate-90" : "opacity-100 rotate-0"
            )}
          />
        </div>
      </div>
      <motion.div
        initial={false}
        animate={{
          height: open ? "auto" : 0,
          opacity: open ? 1 : 0,
        }}
        transition={{ duration: 0.2 }}
        className="overflow-hidden"
      >
        <p className="text-muted-foreground mt-4 pr-12">
          {answer}
        </p>
      </motion.div>
    </button>
  );
}

const questions = [
  {
    question: "What is Lint?",
    answer: "Lint is a creator platform that lets you sell courses, digital downloads, and coaching sessions. We focus on transparent pricing, fast payouts, and built-in community features — everything you need to build a sustainable creator business.",
  },
  {
    question: "How much does it cost to get started?",
    answer: "You can start for free! There's no monthly fee on our Starter plan. We only take a 9% transaction fee when you make a sale. Our Pro plan is $29/month with a reduced 5% transaction fee for creators who want to maximize their earnings.",
  },
  {
    question: "How do payouts work?",
    answer: "We use Stripe Connect for payments. Once you connect your Stripe account, you'll receive automatic payouts on a weekly basis (or daily with Pro). Payouts typically arrive within 2-3 business days after processing.",
  },
  {
    question: "Can I migrate my existing courses?",
    answer: "Yes! You can import your existing content into Lint. Our course builder makes it easy to upload videos, create lessons, and organize your curriculum. If you need help migrating, our support team is here to assist.",
  },
  {
    question: "What payment methods do you support?",
    answer: "Through Stripe, we support all major credit and debit cards, Apple Pay, Google Pay, and many local payment methods depending on your customers' location. We handle all the payment processing so you can focus on creating.",
  },
  {
    question: "Do you offer refunds?",
    answer: "We have a 30-day money-back guarantee on our Pro plan. For student refunds on your courses, you have full control — you can set your own refund policy and handle refund requests through your dashboard.",
  },
];

export function FAQ() {
  return (
    <section id="faq" className="py-16 md:py-24 lg:py-32 bg-background">
      <Container>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="flex justify-center mb-8"
        >
          <div className="w-16 h-16 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
            <HelpCircle className="w-8 h-8 text-muted-foreground" />
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <SectionHeading>Frequently Asked Questions</SectionHeading>
        </motion.div>

        {/* Questions */}
        <div className="max-w-3xl mx-auto space-y-4">
          {questions.map((q, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.4 }}
              viewport={{ once: true }}
            >
              <Question question={q.question} answer={q.answer} />
            </motion.div>
          ))}
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-muted-foreground">
            Still have questions?{" "}
            <a href="mailto:support@uselint.com" className="text-emerald-500 hover:underline">
              Contact our support team
            </a>
          </p>
        </motion.div>
      </Container>
    </section>
  );
}

